import { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '@/lib/adminApi';
import { Search, ChevronRight, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers({ search: search || undefined, limit: 200 });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Permanently delete this user and all their data?')) return;
    try {
      await adminAPI.deleteUser(id);
      toast.success('User deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-users-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>Users</h1>
        <p className="text-purple-200/50 text-sm mt-1">{total} total registered users.</p>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-purple-200/30 focus:outline-none focus:border-purple-400/50 transition"
          data-testid="users-search-input"
        />
      </div>

      <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 text-xs uppercase tracking-wider text-purple-200/50 border-b border-white/5 bg-white/[0.02]">
          <div className="col-span-3">User</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-2">Onboarding</div>
          <div className="col-span-1">Cats</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-8 text-center text-purple-200/50">Loading…</div>}
        {!loading && users.length === 0 && <div className="p-8 text-center text-purple-200/50">No users found.</div>}
        {users.map(u => (
          <button
            key={u.id}
            onClick={() => navigate(`/skyportal/users/${u.id}`)}
            className="w-full grid grid-cols-12 gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/[0.03] transition text-left items-center"
            data-testid={`user-row-${u.id}`}
          >
            <div className="col-span-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-200 text-sm font-semibold">
                {(u.name || u.email || '?')[0]?.toUpperCase()}
              </div>
              <span className="text-white text-sm font-medium truncate">{u.name || '—'}</span>
            </div>
            <div className="col-span-3 text-purple-200/70 text-sm truncate">{u.email}</div>
            <div className="col-span-2 text-purple-200/50 text-xs">{(u.created_at || '').slice(0, 10)}</div>
            <div className="col-span-2">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${u.onboarding_completed ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                {u.onboarding_completed ? 'Done' : 'Pending'}
              </span>
            </div>
            <div className="col-span-1 text-white/80 text-sm">{u.categories_filled || 0}/7</div>
            <div className="col-span-1 flex items-center justify-end gap-1">
              <button onClick={(e) => handleDelete(u.id, e)} className="p-1.5 text-red-300/40 hover:text-red-300 hover:bg-red-500/10 rounded transition">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <ChevronRight className="w-4 h-4 text-purple-300/40" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
